import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc, 
  writeBatch,
  deleteDoc
} from 'firebase/firestore';
import { Bell, Check, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  createdAt: any;
  read: boolean;
  type: 'success' | 'info' | 'warning';
  link?: string;
}

export default function NotificationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Notification[];
      setNotifications(notifs);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching notifications:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const markAllAsRead = async () => {
    if (!user) return;
    const batch = writeBatch(db);
    notifications.filter(n => !n.read).forEach(n => {
      const docRef = doc(db, 'notifications', n.id);
      batch.update(docRef, { read: true });
    });
    try {
      await batch.commit();
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  };

  const markAsRead = async (id: string, link?: string) => {
    try {
      const docRef = doc(db, 'notifications', id);
      await updateDoc(docRef, { read: true });
      if (link) navigate(link);
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  const deleteNotification = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await deleteDoc(doc(db, 'notifications', id));
    } catch (err) {
      console.error("Failed to delete notification:", err);
    }
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return '...';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString();
    } catch (err) {
      return '';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'success': return 'text-green-500 bg-green-500/10';
      case 'warning': return 'text-yellow-500 bg-yellow-500/10';
      default: return 'text-[#FF5C00] bg-[#FF5C00]/10';
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0e12] text-white">
      <main className="py-12 px-6 max-w-2xl mx-auto">
        <div className="flex justify-between items-end mb-8">
          <div>
            <h1 className="text-3xl font-black mb-2">Notifications</h1>
            <p className="text-gray-400">Restez informé de l'activité sur FESTIN</p>
          </div>
          {notifications.some(n => !n.read) && (
            <button 
              onClick={markAllAsRead}
              className="text-sm font-bold text-[#FF5C00] hover:text-[#ff7a33] transition-colors"
            >
              Tout marquer comme lu
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-[#FF5C00] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : notifications.length > 0 ? (
          <div className="space-y-4">
            {notifications.map((notif) => (
              <div 
                key={notif.id}
                onClick={() => markAsRead(notif.id, notif.link)}
                className={`group relative p-4 rounded-2xl border transition-all cursor-pointer ${
                  !notif.read 
                    ? 'bg-white/[0.03] border-[#FF5C00]/30 shadow-[0_0_20px_rgba(255,92,0,0.05)]' 
                    : 'bg-white/[0.01] border-white/5 hover:bg-white/[0.03]'
                }`}
              >
                <div className="flex gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${getTypeColor(notif.type)}`}>
                    {notif.type === 'success' ? <Check className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-1">
                      <h4 className={`font-bold truncate pr-6 ${!notif.read ? 'text-white' : 'text-gray-300'}`}>
                        {notif.title}
                      </h4>
                      {!notif.read && (
                        <span className="absolute top-4 right-4 w-2 h-2 bg-[#FF5C00] rounded-full"></span>
                      )}
                    </div>
                    <p className={`text-sm mb-2 ${!notif.read ? 'text-gray-300' : 'text-gray-500'}`}>
                      {notif.message}
                    </p>
                    <span className="text-[10px] font-bold text-gray-600 uppercase tracking-widest leading-none">
                      {formatTime(notif.createdAt)}
                    </span>
                  </div>
                  
                  <button 
                    onClick={(e) => deleteNotification(e, notif.id)}
                    className="opacity-0 group-hover:opacity-100 p-2 text-gray-600 hover:text-red-500 transition-all absolute bottom-2 right-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white/[0.02] rounded-3xl border border-white/5 border-dashed">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-gray-600" />
            </div>
            <h3 className="font-bold text-lg mb-1">Aucune notification</h3>
            <p className="text-gray-500 text-sm">Vous êtes à jour !</p>
          </div>
        )}
      </main>
    </div>
  );
}
